/**
 * Admin Header Component
 * 
 * Admin panel header with logo and user menu.
 * Displays on all admin pages.
 * 
 * @version 1.0.0
 */

import React from 'react';
import { AppBar, Toolbar, Typography, Box, Avatar, Menu, MenuItem, Paper, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { AccountCircle, Logout as LogoutIcon } from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

const AdminHeader = ({ onLogout }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openLogoutDialog, setOpenLogoutDialog] = React.useState(false);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMyAccount = () => {
    handleClose();
    navigate('/admin/my-account');
  };

  const handleLogoutClick = () => {
    setOpenLogoutDialog(true);
  };

  const handleConfirmLogout = () => {
    setOpenLogoutDialog(false);
    handleClose();
    onLogout();
  };

  const handleCancelLogout = () => {
    setOpenLogoutDialog(false);
  };

  return (
    <AppBar position="fixed" sx={{ backgroundColor: '#1a3a2a', width: '100%', zIndex: 1300 }}>
      <Toolbar disableGutters sx={{ px: 2, py: 1 }}>
        {/* Logo & Title */}
        <Box
          component={RouterLink}
          to="/admin/dashboard"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            gap: 1.5,
            marginRight: 'auto',
            transition: 'all 0.3s ease-in-out',
            '&:hover': {
              opacity: 0.9,
              transform: 'scale(1.05)',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 45,
              width: 45,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.2)',
              transition: 'all 0.3s ease-in-out',
            }}
          >
            <img 
              src="/logo.jpeg" 
              alt="Roots & Wings Logo" 
              style={{
                height: 43,
                width: 43,
                borderRadius: '50%',
                transition: 'filter 0.3s ease-in-out',
              }}
            />
          </Box>
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                lineHeight: 1.2,
                margin: 0,
                transition: 'color 0.3s ease-in-out',
              }}
            >
              Roots & Wings
            </Typography>
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#b8e6b8',
                fontSize: '0.7rem',
                fontWeight: '500',
                transition: 'color 0.3s ease-in-out',
              }}
            >
              Admin Dashboard
            </Typography>
          </Box>
        </Box>

        {/* User Menu */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box
            onClick={handleMenu}
            sx={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: 40,
              width: 40,
              borderRadius: '50%',
              backgroundColor: '#8b3a3a',
              transition: 'all 0.3s ease-in-out',
              '&:hover': {
                backgroundColor: '#a0474a',
                transform: 'scale(1.05)',
              },
            }}
          >
            <Typography
              sx={{
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1rem',
              }}
            >
              SA
            </Typography>
          </Box>
          
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            PaperProps={{
              sx: {
                marginTop: '10px',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                minWidth: '280px',
              },
            }}
          >
            {/* Profile Header Section */}
            <Box sx={{ px: 2, py: 2 }}>
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 'bold',
                  color: '#1a3a2a',
                  fontSize: '1rem',
                }}
              >
                Super Admin
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: '#666',
                  fontSize: '0.85rem',
                }}
              >
                Full Access
              </Typography>
            </Box>
            
            <Divider sx={{ my: 1 }} />
            
            {/* My Account Option */}
            <MenuItem
              onClick={handleMyAccount}
              sx={{
                py: 1.5,
                px: 2,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            >
              <AccountCircle
                sx={{
                  marginRight: '12px',
                  color: '#1a3a2a',
                  fontSize: '1.3rem',
                }}
              />
              <Typography sx={{ color: '#1a3a2a', fontWeight: '500' }}>
                My Account
              </Typography>
            </MenuItem>
            
            <Divider sx={{ my: 1 }} />
            
            {/* Logout Option */}
            <MenuItem
              onClick={handleLogoutClick}
              sx={{
                py: 1.5,
                px: 2,
                '&:hover': {
                  backgroundColor: '#fff5f5',
                },
              }}
            >
              <LogoutIcon
                sx={{
                  marginRight: '12px',
                  color: '#d32f2f',
                  fontSize: '1.3rem',
                }}
              />
              <Typography sx={{ color: '#d32f2f', fontWeight: '500' }}>
                Logout
              </Typography>
            </MenuItem>
          </Menu>

          {/* Logout Confirmation Dialog */}
          <Dialog
            open={openLogoutDialog}
            onClose={handleCancelLogout}
            PaperProps={{
              sx: {
                borderRadius: '8px',
              },
            }}
          >
            <DialogTitle
              sx={{
                fontWeight: 'bold',
                color: '#1a3a2a',
                paddingBottom: 1,
              }}
            >
              Confirm Logout
            </DialogTitle>
            <DialogContent>
              <Typography
                sx={{
                  color: '#666',
                  marginTop: 1,
                }}
              >
                Are you sure you want to logout? You will need to login again to access the admin dashboard.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={handleCancelLogout}
                sx={{
                  color: '#666',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmLogout}
                variant="contained"
                sx={{
                  backgroundColor: '#d32f2f',
                  color: 'white',
                  textTransform: 'none',
                  fontSize: '0.95rem',
                  '&:hover': {
                    backgroundColor: '#b71c1c',
                  },
                }}
              >
                Logout
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default AdminHeader;
